/**
 * connectors.js — Connector domain service.
 * ZAP/OpenVAS CRUD, deployment settings, scanner deployment tokens/attempts.
 */
import {
  toApiUrl,
  fetchWithAuthRetry,
  jsonFetchOptions,
  parseJsonOrThrow,
  readApiErrorMessage,
  requestJson,
} from './_core';

export async function listZapConnectors(token = null) {
  const response = await fetchWithAuthRetry(toApiUrl('/connectors/zap/'), jsonFetchOptions(token), token);
  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Could not load web scanners (${response.status})`);
    throw new Error(msg);
  }
  const data = await parseJsonOrThrow(response, 'Web scanners list');
  return Array.isArray(data) ? data : (data?.results ?? []);
}

export async function createZapConnector(payload, token = null) {
  return requestJson('POST', '/connectors/zap/', payload, token);
}

export async function getZapConnector(settingId, token = null) {
  return requestJson('GET', `/connectors/zap/${settingId}/`, null, token);
}

export async function updateZapConnector(settingId, payload, token = null) {
  return requestJson('PUT', `/connectors/zap/${settingId}/`, payload, token);
}

export async function deleteZapConnector(settingId, token = null) {
  return requestJson('DELETE', `/connectors/zap/${settingId}/`, null, token);
}

export async function testZapConnector(settingId, token = null) {
  return requestJson('POST', `/connectors/zap/${settingId}/actions/test-connection/`, {}, token);
}

export async function listOpenvasConnectors(token = null, query = null) {
  const qs =
    query && typeof query === 'object' && Object.keys(query).length > 0
      ? `?${new URLSearchParams(query).toString()}`
      : '';
  const response = await fetchWithAuthRetry(toApiUrl(`/connectors/openvas/${qs}`), jsonFetchOptions(token), token);
  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Could not load network scanners (${response.status})`);
    throw new Error(msg);
  }
  const data = await parseJsonOrThrow(response, 'Network scanners list');
  return Array.isArray(data) ? data : (data?.results ?? []);
}

export async function createOpenvasConnector(payload, token = null) {
  return requestJson('POST', '/connectors/openvas/', payload, token);
}

export async function getOpenvasConnector(settingId, token = null) {
  return requestJson('GET', `/connectors/openvas/${settingId}/`, null, token);
}

export async function updateOpenvasConnector(settingId, payload, token = null) {
  return requestJson('PUT', `/connectors/openvas/${settingId}/`, payload, token);
}

export async function deleteOpenvasConnector(settingId, token = null) {
  return requestJson('DELETE', `/connectors/openvas/${settingId}/`, null, token);
}

export async function testOpenvasConnector(settingId, token = null) {
  return requestJson('POST', `/connectors/openvas/${settingId}/actions/test-connection/`, {}, token);
}

export async function getDeploymentPlatformSettings(token = null) {
  return requestJson('GET', '/settings/deployment', null, token);
}

export async function updateDeploymentPlatformSettings(payload, token = null) {
  return requestJson('PUT', '/settings/deployment', payload, token);
}

export async function listScannerDeploymentTokens(query = null, token = null) {
  const qs =
    query && typeof query === 'object' && Object.keys(query).length > 0
      ? `?${new URLSearchParams(query).toString()}`
      : '';
  const response = await fetchWithAuthRetry(toApiUrl(`/scanner-installs/tokens${qs}`), jsonFetchOptions(token), token);
  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `List scanner deployment tokens failed (${response.status})`);
    throw new Error(msg);
  }
  const data = await parseJsonOrThrow(response, 'Scanner deployment tokens list');
  return Array.isArray(data) ? data : (data?.results ?? []);
}

export async function createScannerDeploymentToken(payload, token = null) {
  return requestJson('POST', '/scanner-installs/tokens', payload, token);
}

export async function getScannerDeploymentToken(tokenId, token = null) {
  return requestJson('GET', `/scanner-installs/tokens/${encodeURIComponent(tokenId)}`, null, token);
}

export async function revokeScannerDeploymentToken(tokenId, reason = 'Revoked by admin', token = null) {
  return requestJson(
    'POST',
    `/scanner-installs/tokens/${encodeURIComponent(tokenId)}/revoke`,
    { reason: String(reason || '').trim() || 'Revoked by admin' },
    token
  );
}

export async function listScannerInstallAttempts(query = null, token = null) {
  const qs =
    query && typeof query === 'object' && Object.keys(query).length > 0
      ? `?${new URLSearchParams(query).toString()}`
      : '';
  const response = await fetchWithAuthRetry(toApiUrl(`/scanner-installs/attempts${qs}`), jsonFetchOptions(token), token);
  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `List scanner install attempts failed (${response.status})`);
    throw new Error(msg);
  }
  const data = await parseJsonOrThrow(response, 'Scanner install attempts list');
  return Array.isArray(data) ? data : (data?.results ?? []);
}

export async function getScannerInstallAttempt(installId, token = null) {
  return requestJson('GET', `/scanner-installs/attempts/${encodeURIComponent(installId)}`, null, token);
}

export async function listScannerRegisteredScanners(query = null, token = null) {
  const qs =
    query && typeof query === 'object' && Object.keys(query).length > 0
      ? `?${new URLSearchParams(query).toString()}`
      : '';
  const response = await fetchWithAuthRetry(toApiUrl(`/scanner-installs/scanners${qs}`), jsonFetchOptions(token), token);
  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `List registered scanners failed (${response.status})`);
    throw new Error(msg);
  }
  const data = await parseJsonOrThrow(response, 'Registered scanners list');
  return Array.isArray(data) ? data : (data?.results ?? []);
}
